import React, { useContext, useEffect, useState } from "react";
import styles from "./StoreEmployees.module.css";
import StoresHeader from "../../Components/Stores/StoresHeader";
import AddNewStaff from "../../Components/StaffManagement/AddNewStaff";
import { toast } from "react-toastify";
import { BASE_URL_API } from "../../References/URLs";
import AuthContext from "../../store/auth-context";
import ActiveStoreContext from "../../store/ActiveStoreContext";
import { Link } from "react-router-dom";
import {
    BsToggleOff, BsToggleOn, BsPersonPlus, BsSearch,
    BsCalendar3, BsArrowUpRight, BsEnvelope, BsPhone,
    BsShieldCheck, BsShieldX, BsX, BsFunnel,
} from "react-icons/bs";
import { FaSpinner } from "react-icons/fa";
import { HiOutlineUserGroup } from "react-icons/hi";
import { MdOutlineManageAccounts } from "react-icons/md";

// ─── Helpers ──────────────────────────────────────────────────
const getInitials = (name = "") =>
    name.trim().split(/\s+/).slice(0, 2).map(n => n[0]?.toUpperCase() || "").join("");

const PALETTES = [
    { bg: "#dbeafe", color: "#1d4ed8" }, { bg: "#fce7f3", color: "#be185d" },
    { bg: "#d1fae5", color: "#065f46" }, { bg: "#fef3c7", color: "#b45309" },
    { bg: "#ede9fe", color: "#6d28d9" }, { bg: "#ffedd5", color: "#c2410c" },
    { bg: "#cffafe", color: "#0e7490" }, { bg: "#ecfdf5", color: "#047857" },
];
const palette = (name = "") => PALETTES[(name.charCodeAt(0) || 0) % PALETTES.length];

const ROLES = {
    MANAGER: { label: "Manager", bg: "#e0f2fe", color: "#0369a1" },
    STAFF:   { label: "Staff",   bg: "#f0fdf4", color: "#15803d" },
};
const roleMeta = (r = "") => ROLES[r?.toUpperCase()] ?? { label: r || "—", bg: "#f1f5f9", color: "#94a3b8" };

// ─── Main Component ───────────────────────────────────────────
const StoreEmployees = () => {
    const authCtx = useContext(AuthContext);
    const asCtx   = useContext(ActiveStoreContext);
    const activeStore = JSON.parse(asCtx?.activeStore);

    const [togglingId, setTogglingId]   = useState(null);
    const [employees,  setEmployees]    = useState([]);
    const [addingNew,  setAddingNew]    = useState(false);
    const [search,     setSearch]       = useState("");
    const [roleFilter, setRoleFilter]   = useState("ALL");
    const [calFilter,  setCalFilter]    = useState("ALL");
    const [authFilter, setAuthFilter]   = useState("ALL");

    const allRoles = ["ALL", ...Array.from(new Set(employees.map(e => e.role?.toUpperCase()).filter(Boolean)))];

    const filtered = employees.filter(e => {
        const q = search.toLowerCase();
        const matchQ =
            !q || e.name?.toLowerCase().includes(q) ||
            e.mobile?.includes(q) || e.email?.toLowerCase().includes(q) ||
            e.username?.toLowerCase().includes(q) || e.displayName?.toLowerCase().includes(q);
        const matchRole = roleFilter === "ALL" || e.role?.toUpperCase() === roleFilter;
        const matchCal  =
            calFilter === "ALL" ||
            (calFilter === "ON"  && e.isAvailableForCalendar === "1") ||
            (calFilter === "OFF" && e.isAvailableForCalendar !== "1");
        const matchAuth =
            authFilter === "ALL" ||
            (authFilter === "YES" && e.hasAuthCreated === "true") ||
            (authFilter === "NO"  && e.hasAuthCreated !== "true");
        return matchQ && matchRole && matchCal && matchAuth;
    });

    const stat = {
        total:      employees.length,
        calOn:      employees.filter(e => e.isAvailableForCalendar === "1").length,
        authCount:  employees.filter(e => e.hasAuthCreated === "true").length,
        loginOn:    employees.filter(e => e.isLoginEnabled === "1").length,
    };

    const dirty = search || roleFilter !== "ALL" || calFilter !== "ALL" || authFilter !== "ALL";
    const reset = () => { setSearch(""); setRoleFilter("ALL"); setCalFilter("ALL"); setAuthFilter("ALL"); };

    useEffect(() => { if (activeStore) load(); }, [activeStore?.storeIdentifier]);

    const load = async () => {
        try {
            const res  = await fetch(`${BASE_URL_API}getAllStoreEmployees`, {
                method: "POST",
                body: JSON.stringify({ token: authCtx?.token, storeIdentifier: activeStore?.storeIdentifier }),
            });
            const data = await res.json();
            if (data.status === "success")         setEmployees(data.response);
            else if (data.message === "tokenExpired") authCtx.logout();
            else setEmployees([]);
        } catch (e) { console.error(e); }
    };

    const toggleCal = async (id) => {
        setTogglingId(id);
        try {
            const res  = await fetch(`${BASE_URL_API}toggleCalendarAvailablility`, {
                method: "POST",
                body: JSON.stringify({ token: authCtx?.token, worker_id: id }),
            });
            const data = await res.json();
            if (data.status === "success")         { toast("Updated"); load(); }
            else if (data.message === "tokenExpired") authCtx.logout();
        } catch (e) { console.error(e); }
        finally { setTogglingId(null); }
    };

    if (!activeStore) return (
        <div className={styles.wrap}>
            <StoresHeader />
            <div className={styles.splash}>
                <HiOutlineUserGroup size={34} className={styles.splashIco} />
                <p className={styles.splashTitle}>No store selected</p>
                <p className={styles.splashSub}>Pick a store to view its team.</p>
            </div>
        </div>
    );

    return (
        <div className={styles.wrap}>
            {/* <StoresHeader /> */}
            <AddNewStaff isOpen={addingNew} onClose={() => setAddingNew(false)}
                onSuccess={() => { toast("Employee added"); setAddingNew(false); load(); }} />

            <div className={styles.page}>

                {/* Header */}
                <div className={styles.hdr}>
                    <div className={styles.hdrLeft}>
                        <h1 className={styles.hdrTitle}>Team</h1>
                        <span className={styles.hdrStore}>{activeStore?.storeName}</span>
                    </div>
                    <button className={styles.addBtn} onClick={() => setAddingNew(true)}>
                        <BsPersonPlus size={12} /> Add Employee
                    </button>
                </div>

                {/* Stats */}
                <div className={styles.statsGrid}>
                    <Stat n={stat.total}     l="Total Staff"     accent="#0f172a" />
                    <Stat n={stat.calOn}     l="On Calendar"     accent="#0284c7" />
                    <Stat n={stat.authCount} l="Has Login"       accent="#7c3aed" />
                    <Stat n={stat.loginOn}   l="Login Active"    accent="#059669" />
                    <Stat n={stat.total - stat.calOn}     l="Cal. Off"   accent="#f59e0b" />
                    <Stat n={stat.total - stat.authCount} l="No Auth"    accent="#dc2626" />
                </div>

                {/* Toolbar */}
                <div className={styles.toolbar}>
                    <div className={styles.searchBox}>
                        <BsSearch size={11} className={styles.searchIco} />
                        <input
                            className={styles.searchInput}
                            placeholder="Search name, phone, username…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        {search && <button className={styles.xBtn} onClick={() => setSearch("")}><BsX size={13} /></button>}
                    </div>

                    <div className={styles.filterBar}>
                        <BsFunnel size={11} className={styles.funnelIco} />

                        <div className={styles.chipRow}>
                            {allRoles.map(r => (
                                <button key={r} onClick={() => setRoleFilter(r)}
                                    className={`${styles.chip} ${roleFilter === r ? styles.chipActive : ""}`}>
                                    {r === "ALL" ? "All Roles" : r.charAt(0) + r.slice(1).toLowerCase()}
                                </button>
                            ))}
                        </div>

                        <div className={styles.sep} />

                        <div className={styles.chipRow}>
                            {[["ALL","Calendar"],["ON","Cal. On"],["OFF","Cal. Off"]].map(([v,l]) => (
                                <button key={v} onClick={() => setCalFilter(v)}
                                    className={`${styles.chip} ${calFilter === v ? styles.chipActive : ""}`}>{l}</button>
                            ))}
                        </div>

                        <div className={styles.sep} />

                        <div className={styles.chipRow}>
                            {[["ALL","Auth"],["YES","Has Login"],["NO","No Login"]].map(([v,l]) => (
                                <button key={v} onClick={() => setAuthFilter(v)}
                                    className={`${styles.chip} ${authFilter === v ? styles.chipActive : ""}`}>{l}</button>
                            ))}
                        </div>

                        {dirty && (
                            <button className={styles.resetBtn} onClick={reset}>
                                <BsX size={11} /> Reset
                            </button>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className={styles.table}>
                    <div className={styles.thead}>
                        <span className={styles.cEmp}>Employee</span>
                        <span className={styles.cRole}>Role</span>
                        <span className={styles.cContact}>Contact</span>
                        <span className={styles.cAuth}>Login Auth</span>
                        <span className={styles.cCal}><BsCalendar3 size={10}/> Calendar</span>
                        <span className={styles.cAct} />
                    </div>

                    <div className={styles.tbody}>
                        {filtered.length > 0 ? filtered.map((emp, i) => {
                            const pal      = palette(emp.name);
                            const role     = roleMeta(emp.role);
                            const toggling = togglingId === emp.staffIdentifier;
                            const calOn    = emp.isAvailableForCalendar === "1";
                            const hasAuth  = emp.hasAuthCreated === "true";
                            const loginOn  = emp.isLoginEnabled === "1";

                            return (
                                <div key={emp.staffIdentifier} className={styles.trow}
                                    style={{ animationDelay: `${i * 22}ms` }}>

                                    {/* Employee */}
                                    <div className={styles.cEmp}>
                                        <div className={styles.avatar} style={{ background: pal.bg, color: pal.color }}>
                                            {getInitials(emp.name) || "?"}
                                        </div>
                                        <div className={styles.empInfo}>
                                            <span className={styles.empName}>{emp.name || "—"}</span>
                                            {emp.displayName && emp.displayName !== emp.name &&
                                                <span className={styles.empDisplay}>aka "{emp.displayName}"</span>}
                                            <span className={styles.empSid}>{emp.staffIdentifier?.slice(-8)}</span>
                                        </div>
                                    </div>

                                    {/* Role */}
                                    <div className={styles.cRole}>
                                        <span className={styles.rolePill}
                                            style={{ background: role.bg, color: role.color }}>
                                            {role.label}
                                        </span>
                                    </div>

                                    {/* Contact */}
                                    <div className={styles.cContact}>
                                        <span className={emp.mobile ? styles.cLine : styles.cMissing}>
                                            <BsPhone size={9} />
                                            {emp.mobile || "No phone"}
                                        </span>
                                        <span className={emp.email ? styles.cLine : styles.cMissing}>
                                            <BsEnvelope size={9} />
                                            {emp.email || "No email"}
                                        </span>
                                    </div>

                                    {/* Auth */}
                                    <div className={styles.cAuth}>
                                        {hasAuth ? (
                                            <div className={styles.authBlock}>
                                                <div className={styles.authRow}>
                                                    <BsShieldCheck size={10} className={styles.shieldOn} />
                                                    <span className={styles.username}>{emp.username}</span>
                                                </div>
                                                <span className={`${styles.loginBadge} ${loginOn ? styles.loginBadgeOn : styles.loginBadgeOff}`}>
                                                    {loginOn ? "Login enabled" : "Login disabled"}
                                                </span>
                                            </div>
                                        ) : (
                                            <div className={styles.noAuth}>
                                                <BsShieldX size={10} className={styles.shieldOff} />
                                                <span>No auth</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Calendar */}
                                    <div className={styles.cCal}>
                                        <button
                                            className={`${styles.togBtn} ${calOn ? styles.togOn : styles.togOff}`}
                                            onClick={() => toggleCal(emp.staffIdentifier)}
                                            title={calOn ? "Disable" : "Enable"}>
                                            {toggling
                                                ? <FaSpinner size={14} className={styles.spin} />
                                                : calOn
                                                    ? <BsToggleOn  size={22} />
                                                    : <BsToggleOff size={22} />}
                                        </button>
                                        <span className={`${styles.calLabel} ${calOn ? styles.calLabelOn : ""}`}>
                                            {calOn ? "Active" : "Off"}
                                        </span>
                                    </div>

                                    {/* Action */}
                                    <div className={styles.cAct}>
                                        <Link to={`/store/employee/${emp.staffIdentifier}`} className={styles.manageBtn}>
                                            <MdOutlineManageAccounts size={13} />
                                            Manage
                                            <BsArrowUpRight size={9} />
                                        </Link>
                                    </div>
                                </div>
                            );
                        }) : (
                            <div className={styles.empty}>
                                <HiOutlineUserGroup size={28} className={styles.emptyIco} />
                                <span>{dirty ? "No employees match your filters." : "No employees found."}</span>
                                {dirty && <button className={styles.resetBtn} onClick={reset}>Clear filters</button>}
                            </div>
                        )}
                    </div>
                </div>

                <p className={styles.count}>
                    {filtered.length === employees.length
                        ? `${employees.length} employees`
                        : `${filtered.length} of ${employees.length} employees`}
                </p>
            </div>
        </div>
    );
};

const Stat = ({ n, l, accent }) => (
    <div className={styles.statCard}>
        <div className={styles.statLine} style={{ background: accent }} />
        <span className={styles.statN} style={{ color: accent }}>{n}</span>
        <span className={styles.statL}>{l}</span>
    </div>
);

export default StoreEmployees;